import { useSyncExternalStore } from "react";

import { APPLY_FAILURE_LOCATION_NOT_FOUND, type ApplyFailureReason } from "@common/constants/appliedActions";

import { ApplyFailureError, CANNOT_LOCATE_TARGET_MESSAGE, PartialRedlineApply } from "../../../utils/errors";
import { UntrackedWriteRefused } from "../../../utils/untracked-write-guard";

export type LastApplyOutcome = "applied" | "partly_applied" | "failed" | "refused" | "not_found";

export interface LastApplyCell {
  bookmark: string;
  landed: boolean;
  door: string | null;
}

export interface LastApplyResult {
  attemptedAt: string;
  initiatedFrom: string;
  outcome: LastApplyOutcome;
  reason: string | null;
  failureReason: ApplyFailureReason | null;
  landed: number | null;
  total: number | null;
  cells: LastApplyCell[] | null;
  commentRelocation: string | null;
}

class LastApplyResults {
  private readonly resultByActionId = new Map<string, LastApplyResult>();
  private readonly cellsByActionId = new Map<string, LastApplyCell[]>();
  private readonly listeners = new Set<() => void>();

  beginAttempt(actionId: string): void {
    this.cellsByActionId.delete(actionId);
  }

  record(actionId: string, result: LastApplyResult): void {
    const cells = this.cellsByActionId.get(actionId) ?? result.cells;
    this.cellsByActionId.delete(actionId);
    this.resultByActionId.set(actionId, { ...result, cells });
    this.notify();
  }

  recordCells(actionId: string, cells: LastApplyCell[]): void {
    this.cellsByActionId.set(actionId, cells);
    const current = this.resultByActionId.get(actionId);
    if (current === undefined) return;
    this.resultByActionId.set(actionId, { ...current, cells });
    this.notify();
  }

  resultOf(actionId: string): LastApplyResult | null {
    return this.resultByActionId.get(actionId) ?? null;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  forgetAll(): void {
    this.resultByActionId.clear();
    this.cellsByActionId.clear();
    this.notify();
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener());
  }
}

export const lastApplyResults = new LastApplyResults();

export function useLastApplyResult(actionId: string | null): LastApplyResult | null {
  return useSyncExternalStore(
    (listener) => lastApplyResults.subscribe(listener),
    () => (actionId === null ? null : lastApplyResults.resultOf(actionId)),
    () => null,
  );
}

const resultBase = (attemptedAt: string, initiatedFrom: string): Omit<LastApplyResult, "outcome"> => ({
  attemptedAt,
  initiatedFrom,
  reason: null,
  failureReason: null,
  landed: null,
  total: null,
  cells: null,
  commentRelocation: null,
});

export function lastApplyResultOfOutcome(applied: boolean, attemptedAt: string, initiatedFrom: string): LastApplyResult {
  if (applied) return { ...resultBase(attemptedAt, initiatedFrom), outcome: "applied" };
  return { ...resultBase(attemptedAt, initiatedFrom), outcome: "not_found", reason: CANNOT_LOCATE_TARGET_MESSAGE, failureReason: APPLY_FAILURE_LOCATION_NOT_FOUND };
}

export function lastApplyResultOfError(error: unknown, attemptedAt: string, initiatedFrom: string): LastApplyResult {
  const base = resultBase(attemptedAt, initiatedFrom);
  if (error instanceof PartialRedlineApply) return { ...base, outcome: "partly_applied", reason: error.message, landed: error.landed, total: error.total };
  if (error instanceof UntrackedWriteRefused) return { ...base, outcome: "refused", reason: error.message };
  if (error instanceof ApplyFailureError) {
    return { ...base, outcome: error.reason === APPLY_FAILURE_LOCATION_NOT_FOUND ? "not_found" : "failed", reason: error.message, failureReason: error.reason };
  }
  return { ...base, outcome: "failed", reason: error instanceof Error ? error.message : String(error) };
}
